import React, { useState, useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';
import { GlobalStyle } from './styles/GlobalStyle';
import BottomNavBar from './components/BottomNavBar';
import MemoryWall from './components/MemoryWall';
import AuthModal from './components/AuthModal';
import ComingSoon from './pages/ComingSoon';
import { Modal } from './components/ui';
import AddMemoryForm from './components/AddMemoryForm';
import InstallPwaPrompt from './components/InstallPwaPrompt';
import { authService, AuthState } from './services/AuthService';
import SuccessScreen from './components/SuccessScreen';
import { requestNotificationPermission, onMessageListener } from './firebase';
import { memoryService } from './services/MemoryService';

const AppContainer = styled.div`
  min-height: 100vh;
`;

interface InitialFormData {
  initialTitle?: string;
  initialContent?: string;
  initialImageUrl?: string;
  initialSongUrl?: string;
}

const App: React.FC = () => {
  const [authState, setAuthState] = useState<AuthState>(authService.getAuthState());
  const [activeNavItem, setActiveNavItem] = useState<'home' | 'discover'>('home');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState<any>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [initialFormData, setInitialFormData] = useState<InitialFormData | null>(null);
  const [memoryWallKey, setMemoryWallKey] = useState(0);
  const [highlightNewestMemory, setHighlightNewestMemory] = useState(false);

  useEffect(() => {
    // Register the service worker with Firebase config
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered with scope:', registration.scope);
        })
        .catch((err) => {
          console.error('Service Worker registration failed:', err);
        });
    }

    const unsubscribe = authService.onAuthStateChange(setAuthState);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authState.isAuthenticated) {
      requestNotificationPermission().then(token => {
        if (token) {
          // Send token to your backend
          console.log('FCM Token registered with backend');
          memoryService.registerFCMToken(token);
        }
      });

      onMessageListener()
        .then(payload => {
          console.log('Received foreground message: ', payload);
          // Refresh the memory wall and highlight the new memory
          setMemoryWallKey(prevKey => prevKey + 1);
          setHighlightNewestMemory(true);
        })
        .catch(err => console.log('failed: ', err));
    }
  }, [authState.isAuthenticated]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareUrl = params.get('url');
    const shareTitle = params.get('title');
    const shareText = params.get('text');

    if (shareUrl) {
      setInitialFormData({
        initialTitle: shareTitle || '',
        initialContent: shareText || `See this! ${shareUrl}`,
        initialSongUrl: shareUrl,
      });
      setIsAddModalOpen(true);
    }
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setShowInstallPrompt(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
  };

  const renderContent = () => {
    switch (activeNavItem) {
      case 'home':
        return <MemoryWall key={memoryWallKey} highlightNewest={highlightNewestMemory} onHighlightComplete={() => setHighlightNewestMemory(false)} />;
      case 'discover':
        return <ComingSoon />;
      default:
        return <MemoryWall key={memoryWallKey} highlightNewest={highlightNewestMemory} onHighlightComplete={() => setHighlightNewestMemory(false)} />;
    }
  };

  if (showInstallPrompt) {
    return <InstallPwaPrompt onInstallClick={handleInstallClick} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AppContainer>
        {authState.isAuthenticated ? (
          <>
            {renderContent()}
            <BottomNavBar
              activeItem={activeNavItem}
              onItemClick={(item) => setActiveNavItem(item as 'home' | 'discover')}
              onAddClick={() => setIsAddModalOpen(true)}
            />
            <Modal 
              isOpen={isAddModalOpen} 
              onClose={() => setIsAddModalOpen(false)}
              title="Send a Ping!"
            >
              <AddMemoryForm
                {...initialFormData}
                onSuccess={() => {
                  setIsAddModalOpen(false);
                  setShowSuccessScreen(true);
                }}
              />
            </Modal>
            <Modal isOpen={showSuccessScreen} onClose={() => setShowSuccessScreen(false)}>
              <SuccessScreen
                onAddAnother={() => {
                  setShowSuccessScreen(false);
                  setIsAddModalOpen(true);
                }}
                onGoToWall={() => {
                  setShowSuccessScreen(false);
                  setMemoryWallKey(prevKey => prevKey + 1);
                }}
              />
            </Modal>
          </>
        ) : (
          <AuthModal />
        )}
      </AppContainer>
    </ThemeProvider>
  );
};

export default App;